import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, MessagesSquare, Users, Building2, CalendarDays, Link2, Bot, FlaskConical, Smartphone, BarChart3,
  UserCog, Settings, Moon, Sun, Bell, LogOut, Menu, X, Crown, Layers,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, publicMedia, ROLE_LABEL, dt } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const TENANT_NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/inbox", label: "Caixa de entrada", icon: MessagesSquare },
  { to: "/leads", label: "Leads / CRM", icon: Users },
  { to: "/imoveis", label: "Imóveis", icon: Building2 },
  { to: "/visitas", label: "Agenda de visitas", icon: CalendarDays },
  { to: "/links", label: "Links inteligentes", icon: Link2, roles: ["admin", "manager"] },
  { to: "/ia", label: "Assistente IA", icon: Bot, roles: ["admin", "manager"] },
  { to: "/simulador", label: "Simulador", icon: FlaskConical, roles: ["admin", "manager"] },
  { to: "/whatsapp", label: "WhatsApp", icon: Smartphone, roles: ["admin", "manager"] },
  { to: "/relatorios", label: "Relatórios", icon: BarChart3, roles: ["admin", "manager"] },
  { to: "/equipe", label: "Equipe", icon: UserCog, roles: ["admin", "manager"] },
  { to: "/configuracoes", label: "Configurações", icon: Settings, roles: ["admin"] },
];
const ADMIN_NAV = [
  { to: "/admin", label: "Imobiliárias", icon: Crown, end: true },
  { to: "/admin/planos", label: "Planos", icon: Layers },
];

function useTheme() {
  const [dark, setDark] = useState(() => localStorage.getItem("theme") === "dark");
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);
  return [dark, setDark];
}

function Brand({ user }) {
  const t = user.tenant;
  const logo = t?.branding?.logo_file_id;
  return (
    <div className="flex items-center gap-3 px-5 py-6 border-b border-white/10" data-testid="sidebar-brand">
      {logo ? (
        <img src={publicMedia(logo)} alt="Logo" className="h-10 w-10 rounded-lg object-contain bg-white p-1" />
      ) : (
        <div className="h-10 w-10 rounded-lg bg-[#D4AF37] text-[#073B2B] grid place-items-center font-heading font-extrabold">{(t?.branding?.display_name || "ImobiAI")[0]}</div>
      )}
      <div className="min-w-0">
        <div className="font-heading font-bold text-white truncate">{t?.branding?.display_name || t?.name || "ImobiAI"}</div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">ImobiAI</div>
      </div>
    </div>
  );
}

function NavItems({ items, onClick }) {
  return items.map(({ to, label, icon: Icon, end }) => (
    <NavLink key={to} to={to} end={end} onClick={onClick} data-testid={`nav-${to.replace(/\//g, "-").slice(1) || "dashboard"}`}
      className={({ isActive }) => `flex items-center gap-3 px-4 py-2.5 mx-3 rounded-lg text-sm transition-colors duration-200 ${isActive ? "bg-white/10 text-white border-l-2 border-[#D4AF37]" : "text-emerald-100/70 hover:text-white hover:bg-white/5"}`}>
      <Icon className="h-4 w-4 shrink-0" />{label}
    </NavLink>
  ));
}

function Notifications() {
  const [data, setData] = useState({ items: [], unread: 0, waiting_human: 0 });
  const nav = useNavigate();
  const lastSeen = useRef(null);
  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const { data: d } = await api.get("/notifications");
        if (!alive) return;
        const newest = d.items[0];
        if (lastSeen.current && newest && newest.id !== lastSeen.current && newest.type === "handoff") toast.warning(newest.title, { description: newest.body });
        lastSeen.current = newest?.id || lastSeen.current || "none";
        setData(d);
      } catch { /* ignore */ }
    };
    poll();
    const id = setInterval(poll, 8000);
    return () => { alive = false; clearInterval(id); };
  }, []);
  const markRead = async () => { await api.post("/notifications/read"); setData((d) => ({ ...d, unread: 0, items: d.items.map((i) => ({ ...i, read: true })) })); };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notifications-bell">
          <Bell className="h-5 w-5" />
          {data.unread > 0 && <span data-testid="notifications-count" className="absolute -top-0.5 -right-0.5 h-5 min-w-5 px-1 rounded-full bg-[#D4AF37] text-[#073B2B] text-[10px] font-bold grid place-items-center">{data.unread}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 bg-popover">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <span className="font-heading font-semibold text-sm">Notificações</span>
          <button className="text-xs text-muted-foreground hover:text-foreground" onClick={markRead} data-testid="notifications-mark-read">Marcar como lidas</button>
        </div>
        {data.waiting_human > 0 && <div className="px-4 py-2 text-xs bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300">{data.waiting_human} atendimento(s) aguardando corretor</div>}
        <div className="max-h-80 overflow-auto">
          {data.items.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">Sem notificações</div>}
          {data.items.map((n) => (
            <button key={n.id} onClick={() => nav(n.link)} className={`w-full text-left px-4 py-3 border-b last:border-0 hover:bg-muted/60 ${n.read ? "opacity-60" : ""}`} data-testid={`notification-${n.id}`}>
              <div className="text-sm font-medium">{n.title}</div>
              <div className="text-xs text-muted-foreground line-clamp-2">{n.body}</div>
              <div className="text-[10px] text-muted-foreground mt-1 font-mono">{dt(n.created_at)}</div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const [dark, setDark] = useTheme();
  const [open, setOpen] = useState(false);
  const isSuper = user.role === "superadmin";
  const items = isSuper ? ADMIN_NAV : TENANT_NAV.filter((i) => !i.roles || i.roles.includes(user.role));

  const sidebar = (
    <div className="flex flex-col h-full bg-[#06382B] dark:bg-[#041F18]">
      <Brand user={user} />
      <nav className="flex-1 overflow-auto py-4 space-y-1"><NavItems items={items} onClick={() => setOpen(false)} /></nav>
      <div className="p-4 border-t border-white/10">
        <div className="text-white text-sm font-medium truncate" data-testid="sidebar-user-name">{user.name}</div>
        <div className="text-[10px] uppercase tracking-wider text-[#D4AF37]" data-testid="sidebar-user-role">{ROLE_LABEL[user.role]}</div>
        <button onClick={logout} data-testid="logout-button" className="mt-3 flex items-center gap-2 text-emerald-100/70 hover:text-white text-xs"><LogOut className="h-3.5 w-3.5" />Sair</button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64 z-30">{sidebar}</aside>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex" data-testid="mobile-drawer">
          <div className="w-72 h-full shadow-2xl">{sidebar}</div>
          <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
        </div>
      )}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 h-16 flex items-center justify-between gap-3 px-4 md:px-8 border-b bg-background/80 backdrop-blur-xl">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-button">{open ? <X /> : <Menu />}</Button>
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground hidden sm:block">{isSuper ? "Administração da plataforma" : user.tenant?.name}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="hidden sm:inline text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-md border border-accent/50 text-accent-foreground bg-accent/15 dark:text-accent" data-testid="topbar-role-badge">{ROLE_LABEL[user.role]}</span>
            <Button variant="ghost" size="icon" onClick={() => setDark(!dark)} data-testid="theme-toggle">{dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}</Button>
            {!isSuper && <Notifications />}
          </div>
        </header>
        <main className="p-4 md:p-8 pb-24 lg:pb-8 max-w-[1600px] mx-auto"><Outlet /></main>
      </div>
      {!isSuper && (
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 grid grid-cols-4 border-t bg-background/95 backdrop-blur-xl" data-testid="mobile-bottom-nav">
          {TENANT_NAV.slice(0, 5).filter((i) => i.to !== "/imoveis").map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} className={({ isActive }) => `flex flex-col items-center py-2 text-[10px] gap-1 ${isActive ? "text-primary dark:text-accent font-semibold" : "text-muted-foreground"}`}>
              <Icon className="h-5 w-5" />{label.split(" ")[0]}
            </NavLink>
          ))}
        </nav>
      )}
    </div>
  );
}
